import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const scope = params.get('scope') || 'tenant';
    const activating = params.get('activate') === '1';
    const navigate = useNavigate();
    const [pwd, setPwd] = useState('');
    const [pwd2, setPwd2] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 8) { toast.error('Mínimo 8 caracteres'); return; }
        if (pwd !== pwd2) { toast.error('Las contraseñas no coinciden'); return; }
        setLoading(true);
        try {
            await axios.post(`${API}/auth/password/reset`, { token, new_password: pwd });
            setDone(true);
            toast.success('Contraseña actualizada');
            setTimeout(() => navigate(scope === 'admin' ? '/admin/login' : '/login'), 1500);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al restablecer contraseña');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="w-full max-w-md card-aviation" data-testid="reset-invalid">
                    <CardHeader><CardTitle className="text-red-500 flex items-center gap-2"><XCircle className="w-5 h-5" /> Enlace inválido</CardTitle></CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">Falta el token en la URL o está mal formado.</p>
                        <Link to={scope === 'admin' ? '/admin/login' : '/login'} className="text-primary text-sm hover:underline">Volver al login</Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md card-aviation">
                <CardHeader><CardTitle className="font-['Barlow_Condensed'] text-2xl uppercase" data-testid="reset-title">{activating ? 'Activa tu cuenta' : 'Nueva Contraseña'}</CardTitle></CardHeader>
                <CardContent>
                    {done ? (
                        <div className="text-center space-y-3 py-4" data-testid="reset-success">
                            <CheckCircle className="w-12 h-12 mx-auto text-emerald-500" />
                            <p className="text-sm">Contraseña restablecida. Redirigiendo al login...</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-pwd">Nueva contraseña (mín. 8, con mayúscula, minúscula, número y símbolo)</Label>
                                <Input id="new-pwd" type="password" required minLength={8} value={pwd} onChange={e => setPwd(e.target.value)} data-testid="reset-new-password" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new-pwd2">Confirmar contraseña</Label>
                                <Input id="new-pwd2" type="password" required minLength={8} value={pwd2} onChange={e => setPwd2(e.target.value)} data-testid="reset-confirm-password" />
                            </div>
                            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="reset-submit">
                                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                {activating ? 'Activar cuenta' : 'Restablecer contraseña'}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
